import { useSession } from '../Components/Session';
import API from '../Services/API';
import Model, { invalidateStateKeys, makeStateKey } from './Model';
import State from './State';

const isPendingPerk = (perk) => {
    return !perk?.claimedAt && !perk?.declinedAt && !perk?.expiredAt;
};

const perkTitle = (perk, fallback = 'Perk') => {
    return perk?.name || fallback;
};

const useMyPerks = (alwaysRefresh = false) => {
    return Model({
        alwaysRefresh,
        stateKey: ['myPerks'],
        fetchData: () => API.get('/perks/mine'),
        returnDataKey: 'perks',
        saveAdditionalData: (perks) => {
            (perks ?? []).forEach((perk) => {
                State.putIfNotExists(['perk', perk.id], perk);
            });
        },
        defaultLoadingError: 'Sorry, there was an error loading your perks.',
    });
};

const usePerk = (perkGivenId, alwaysRefresh = false) => {
    return Model({
        alwaysRefresh,
        stateKey: ['perk', perkGivenId],
        block: !perkGivenId,
        fetchData: () => API.get(`/perks/${perkGivenId}`),
        returnDataKey: 'perk',
        defaultLoadingError: 'Sorry, there was an error loading the perk.',
        registerAdditionalMethods: (opts) => {
            return {
                claim: () => {
                    return opts.wrapUpdateCall(
                        async () => {
                            await API.post(`/perks/${perkGivenId}/claim`);
                            await opts.refresh();
                        },
                        null,
                        false,
                    ).then(() => {
                        invalidateStateKeys([makeStateKey(['myPerks'])]);
                        return opts.model();
                    });
                },
                decline: () => {
                    return opts.wrapUpdateCall(
                        async () => {
                            await API.post(`/perks/${perkGivenId}/decline`);
                            await opts.refresh();
                        },
                        null,
                        false,
                    ).then(() => {
                        invalidateStateKeys([makeStateKey(['myPerks'])]);
                        return opts.model();
                    });
                },
            };
        },
    });
};

const perkGivenStatus = (given) => {
    if (given?.claimedAt) {
        return { label: 'Claimed', theme: 'success' };
    }

    if (given?.declinedAt) {
        return { label: 'Declined', theme: 'secondary' };
    }

    if (given?.expiredAt) {
        return { label: 'Expired', theme: 'danger' };
    }

    return { label: 'Pending', theme: 'warning' };
};

const useAdminPerksGiven = (alwaysRefresh = false) => {
    return Model({
        alwaysRefresh,
        stateKey: ['admin', 'perks', 'given'],
        fetchData: () => API.get('/admin/perks/given'),
        returnDataKey: 'given',
        defaultLoadingError: 'Sorry, there was an error loading perk assignments.',
        registerAdditionalMethods: (opts) => ({
            expireGiven: (perkGivenId) => {
                return opts.wrapUpdateCall(
                    () => API.post(`/admin/perks/given/${perkGivenId}/expire`),
                    'Perk expired',
                    true,
                );
            },
            expireMany: (perkGivenIds) => {
                return opts.wrapUpdateCall(
                    () => API.post('/admin/perks/given/expireMany', { perkGivenIds }),
                    'Perks expired',
                    true,
                );
            },
        }),
    });
};

const useAdminPerks = (alwaysRefresh = false) => {
    const {isAdmin} = useSession();

    return Model({
        block: !isAdmin(),
        alwaysRefresh,
        stateKey: ['admin', 'perks'],
        fetchData: () => API.get('/admin/perks'),
        returnDataKey: 'perks',
        defaultLoadingError: 'Sorry, there was an error loading perks.',
        registerAdditionalMethods: (opts) => ({
            createPerk: (data) => {
                return opts.wrapUpdateCall(
                    () => API.post('/admin/perks', data),
                    'Perk created',
                    true,
                );
            },
            updatePerk: (perkId, data) => {
                return opts.wrapUpdateCall(
                    () => API.post(`/admin/perks/${perkId}`, data),
                    'Perk updated',
                    true,
                );
            },
            deletePerk: (perkId) => {
                return opts.wrapUpdateCall(
                    () => API.delete(`/admin/perks/${perkId}`),
                    'Perk deleted',
                    true,
                );
            },
            addUrls: (perkId, urls) => {
                return opts.wrapUpdateCall(
                    () => API.post(`/admin/perks/${perkId}/urls`, { urls }),
                    'URLs added',
                    true,
                );
            },
        }),
    });
};

export {
    useMyPerks,
    usePerk,
    useAdminPerks,
    useAdminPerksGiven,
    isPendingPerk,
    perkGivenStatus,
    perkTitle,
};
